import { TFunction } from 'i18next';

import { ClientConfiguration } from '@Models';
import config from '../../../config';
import { Flavor, MenuItem } from '../types';
import buildDashboardItems from './dashboards';
import buildDefaultMenu from './default';
import buildGs1Menu from './gs1';
import buildLockedMenu from './locked';
import buildLogycaMenu from './logyca';

/** Resolve the current flavor from build-time config flags. */
export const getActiveFlavor = (): Flavor => {
  if (config.isGs1Flavor) return 'gs1';
  if (config.isLogycaFlavor) return 'logyca';
  return 'default';
};

interface BuildMenuArgs {
  configuration: ClientConfiguration | undefined;
  permissions: string[];
  t: TFunction;
}

/**
 * Apply permission and configuration gating to a built menu tree.
 *
 * Pre-resolved items (those with explicit `enabled` set) are passed through
 * untouched, mirroring the legacy short-circuit in sidebar.container.tsx.
 *
 * Items without `enabled` are validated against permissions and
 * configurationKey. Submenu parents are dropped if NO child has a valid
 * permission — matches the legacy "hasValidSubmenu" check.
 */
const applyGating = (
  items: MenuItem[],
  permissions: string[],
  configuration: ClientConfiguration | undefined,
): MenuItem[] =>
  items
    .map((item): MenuItem | null => {
      if (Object.prototype.hasOwnProperty.call(item, 'enabled')) return item;

      const { permission, configurationKey, hideWhenDisabled, hide } = item;

      if (hide) return null;

      const hasPermission = (p: typeof permission) =>
        Array.isArray(p) ? p.some((x) => permissions.includes(x)) : !!p && permissions.includes(p);

      const needsValidation = !!(permission || configurationKey);
      const validPermission = hasPermission(permission);
      const validConfiguration = !!(configurationKey && configuration?.[configurationKey]);
      const enabled = !needsValidation || validPermission || validConfiguration;

      if (item.submenu) {
        const hasValidChild = item.submenu.some((child) => !child.permission || hasPermission(child.permission));
        if (!hasValidChild) return null;
      }

      if (hideWhenDisabled && !enabled) return null;

      return { ...item, enabled };
    })
    .filter((item): item is MenuItem => item !== null);

/** Pure menu builder — same inputs, same output. Safe to memoize at the caller. */
export const buildMenu = ({ configuration, permissions, t }: BuildMenuArgs): MenuItem[] => {
  const dashboards = buildDashboardItems(configuration?.dashboards, t);

  const builders = { gs1: buildGs1Menu, logyca: buildLogycaMenu, default: buildDefaultMenu } as const;
  const flavor = getActiveFlavor();
  const builder = builders[flavor as keyof typeof builders] ?? buildDefaultMenu;
  const rawMenu =
    builder === buildDefaultMenu ? buildDefaultMenu(configuration, t, permissions) : builder(configuration, t);

  return applyGating([...dashboards, ...rawMenu], permissions, configuration);
};

export { buildLockedMenu };
